import type { CalendarViewProps } from './shared'
import { DAY_NAMES, EmptyState, EventCard, getDatePart, getWeekDays, toLocalDateString } from './shared'
import { EventShell } from './BookingActions'
import { cn } from '@/lib/utils'

const TODAY = toLocalDateString(new Date())

export function WeekCalendarView({ data, interactive, onChanged }: CalendarViewProps) {
  const events = data.events ?? []
  const weekDays = getWeekDays(data.date)
  const weekLabel = `${weekDays[0].toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })} - ${weekDays[6].toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`

  if (events.length === 0) {
    return (
      <div>
        <h3 className="border-b border-border bg-muted px-4 py-2.5 text-sm font-bold text-foreground">{weekLabel}</h3>
        <EmptyState />
      </div>
    )
  }

  return (
    <div>
      <h3 className="border-b border-border bg-muted px-4 py-2.5 text-sm font-bold text-foreground">{weekLabel}</h3>
      <div className="max-h-[620px] overflow-x-auto">
        <div className="flex gap-1.5 px-2 py-2">
          {weekDays.map((day, index) => {
            const dateStr = toLocalDateString(day)
            const isToday = dateStr === TODAY
            const dayEvents = events
              .filter(event => getDatePart(event.start) === dateStr)
              .sort((a, b) => a.start.localeCompare(b.start))

            return (
              <div key={dateStr} className={cn('w-[150px] shrink-0 px-[5px] py-2', isToday && 'rounded-md bg-blue-50')}>
                <div className="mb-2 flex flex-col items-center">
                  <span className={cn('text-center text-[10px] font-bold uppercase text-muted-foreground', isToday && 'text-blue-600')}>{DAY_NAMES[index]}</span>
                  <span className={cn('mt-0.5 text-center text-lg font-semibold text-foreground', isToday && 'text-blue-600')}>{day.getDate()}</span>
                  {dayEvents.length > 0 ? (
                    <span className={cn('mt-1 min-w-5 rounded-full bg-border px-1.5 py-px text-center text-[11px] font-bold text-muted-foreground', isToday && 'bg-blue-100 text-blue-700')}>{dayEvents.length}</span>
                  ) : null}
                </div>
                <div className="max-h-[520px] overflow-y-auto">
                  <div className="flex min-h-12 flex-col gap-1">
                    {dayEvents.length === 0 ? (
                      <div className="mt-1 h-[3px] rounded-[2px] bg-muted" />
                    ) : (
                      dayEvents.map(event => (
                        <EventShell
                          key={event.id}
                          event={event}
                          interactive={interactive}
                          onChanged={onChanged}
                          className="block w-full"
                        >
                          <EventCard event={event} compact />
                        </EventShell>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
